import { useState } from 'react';
import { useEffectOnce } from 'react-use';

import { getDefaultTimeRange } from '@grafana/data';
import { SceneDataLayerControls, SceneTimeRange, VariableValueSelectors } from '@grafana/scenes';
import { ReportDashboardV2 } from 'app/extensions/types/reports';
import { DashboardScene } from 'app/features/dashboard-scene/scene/DashboardScene';

import { SelectDashboardScene } from './SelectDashboardScene';

export const useSelectDashboards = ({
  dashboardScene,
  addDashboard,
  removeDashboard,
  dashboards,
}: {
  dashboardScene?: DashboardScene;
  addDashboard: (dashboard: ReportDashboardV2) => void;
  removeDashboard: (index: number) => void;
  dashboards?: ReportDashboardV2[];
}) => {
  const [sceneDashboards, setSceneDashboards] = useState<SelectDashboardScene[]>([]);

  useEffectOnce(() => {
    const newDashboardScene = createSelectDashboardScene(dashboardScene);
    setSceneDashboards([newDashboardScene]);
    addDashboard({
      uid: newDashboardScene?.state.uid,
      key: newDashboardScene?.state.key,
      timeRange: newDashboardScene?.state.$timeRange?.state.value || getDefaultTimeRange(),
    });
  });

  const onRemoveDashboardClick = (dashboard: SelectDashboardScene) => {
    const index = dashboards?.findIndex((d) => d.key === dashboard.state.key);
    if (index !== undefined) {
      removeDashboard(index);
      setSceneDashboards((prevState) => prevState.filter((d) => d !== dashboard));
    }
  };

  const createSelectDashboardScene = (dashboard?: DashboardScene) => {
    return new SelectDashboardScene({
      key: dashboard?.state.key,
      uid: dashboard?.state.uid,
      title: dashboard?.state.title,
      $timeRange: dashboard?.state.$timeRange?.clone() || new SceneTimeRange({ value: getDefaultTimeRange() }),
      $variables: dashboard?.state.$variables?.clone(),
      variableControls: [new VariableValueSelectors({ layout: 'vertical' }), new SceneDataLayerControls()],
      onRemoveClick: onRemoveDashboardClick,
    });
  };

  const onAddDashboardClick = () => {
    const customScene = createSelectDashboardScene();

    setSceneDashboards((prevState) => [...prevState, customScene]);
    addDashboard({
      uid: undefined,
      key: customScene.state.key,
      timeRange: customScene.state.$timeRange?.state.value || getDefaultTimeRange(),
    });
  };

  return {
    sceneDashboards,
    onAddDashboardClick,
  };
};
